import { FEET_10_IN_MM, FEET_20_IN_MM } from "../consts";
import { feetToMillimeters, roundDec } from "@tedivo/tedivo-pure-helpers";

import { IBayPattern } from "../types/IPositionPatterns";
import { IGetBayBlocksOutput } from "../types/IGetBayBlocksOutput";
import { IVesselParts } from "open-vessel-definition";
import TContainerLengths from "../TContainerLengths";
import { preCalculateCurrentVesselParts } from "../preCalculateCurrentVesselParts";

export function calculateMissingLcgs({
  missingImportantLcgs,
  allIsoBays,
  blockBaysAndSizes,
  blockBaysAndSizesByBlockBay,
  blockBaysAndSizesBy20Bay,
  vesselPartsData,
  containerLengths,
}: {
  missingImportantLcgs: boolean | undefined;
  vesselPartsData: IVesselParts[];
  containerLengths: TContainerLengths[];
} & IGetBayBlocksOutput): {
  missingLcgs: TAllBayLcgs;
} {
  const missingLcgs: TAllBayLcgs = {};

  if (!missingImportantLcgs) return { missingLcgs };

  const FEET_40_IN_MM = feetToMillimeters(40);
  const SEPARATION_IN_BETWEEN_BAYS = feetToMillimeters(6);

  const all40Lengths = ([40, 45, 48, 53] as TContainerLengths[]).filter(
    (l) => containerLengths.length === 0 || containerLengths.includes(l),
  );

  const allVesselParts = preCalculateCurrentVesselParts(allIsoBays, vesselPartsData);

  /** Contains the remaining distance of non-bay slots */
  let remainingPartsLength = allVesselParts.reduce((acc, vp) => {
    if (typeof vp === "string") return acc;
    return acc + vp.len;
  }, 0);

  /** Total length of bays with separations */
  const totalBaysLength = blockBaysAndSizes.reduce(
    (acc, b) => acc + feetToMillimeters(b.blockSize),
    (blockBaysAndSizes.length - 1) * SEPARATION_IN_BETWEEN_BAYS,
  );

  let i = 0;
  let remainingBaysLength = totalBaysLength;
  let prevBlock: string | undefined = undefined;

  /** Replace single-paired bays by one double in allVesselParts */
  while (i < allVesselParts.length) {
    const vp = allVesselParts[i] as IBayPattern;
    if (typeof vp !== "string") {
      i++;
      continue;
    }

    const block = blockBaysAndSizesBy20Bay[vp];
    if (block?.numberOfBays === 2) {
      // Get both bays
      const pBays = block.allBays;
      // Substitute single by double
      allVesselParts[i] = pBays;
      // Find the paired bay
      const bayPaired = pBays.split("-").filter((c) => c !== vp)[0];
      const idx = allVesselParts.findIndex((v) => v === bayPaired);
      // Remove it
      allVesselParts.splice(idx, 1);
      i += 1;
    } else {
      i++;
    }
  }

  /** Iterate from 001 to last bay, to obtain LCGs */
  allVesselParts.forEach((vp) => {
    if (typeof vp === "string") {
      const block = blockBaysAndSizesByBlockBay[vp];
      if (!block) return;

      const sep =
        prevBlock === undefined || prevBlock === block.allBays ? 0 : SEPARATION_IN_BETWEEN_BAYS;

      prevBlock = block.allBays;

      if (block.numberOfBays === 2) {
        const blockSize = block.blockSize;

        // As we are adding a separation between 20s, we need to add the separation to the blockSize
        const blockSizeInMm = Math.max(feetToMillimeters(blockSize), FEET_40_IN_MM);

        const fore40Lcg = remainingBaysLength + remainingPartsLength - sep;

        const lcgsInfoOf4x = {
          foreLcg: fore40Lcg,
          lcg: fore40Lcg - roundDec(blockSizeInMm * 0.5, 0),
          aftLcg: fore40Lcg - blockSizeInMm,
        };

        const lcgsInfoOfFore20 = {
          foreLcg: lcgsInfoOf4x.lcg + FEET_20_IN_MM,
          lcg: lcgsInfoOf4x.lcg + FEET_10_IN_MM,
          aftLcg: lcgsInfoOf4x.lcg,
        };

        const lcgsInfoOfAft20 = {
          foreLcg: lcgsInfoOf4x.lcg,
          lcg: lcgsInfoOf4x.lcg - FEET_10_IN_MM,
          aftLcg: lcgsInfoOf4x.lcg - FEET_20_IN_MM,
        };

        const [vpFore, vpAft] = block.allBays.split("-").sort() as IBayPattern[];

        if (!missingLcgs[vpFore]) missingLcgs[vpFore] = new Map();
        if (!missingLcgs[vpAft]) missingLcgs[vpAft] = new Map();

        all40Lengths.forEach((l) => {
          missingLcgs[vpFore].set?.(l, lcgsInfoOf4x);
          missingLcgs[vpAft].set?.(l, lcgsInfoOf4x);
        });

        missingLcgs[vpFore].set?.(20, lcgsInfoOfFore20);
        missingLcgs[vpAft].set?.(20, lcgsInfoOfAft20);

        remainingBaysLength = remainingBaysLength - sep - blockSizeInMm;
      } else {
        const blockSize = block.blockSize;
        const blockSizeInMm = feetToMillimeters(blockSize);

        const fore20Lcg = roundDec(remainingBaysLength + remainingPartsLength - sep, 0);
        const mid20Lcg = roundDec(fore20Lcg - blockSizeInMm * 0.5, 0);
        const aft20Lcg = roundDec(fore20Lcg - blockSizeInMm, 0);

        const lcgsInfoOf20 = {
          foreLcg: fore20Lcg,
          lcg: mid20Lcg,
          aftLcg: aft20Lcg,
        };

        if (!missingLcgs[block.base]) missingLcgs[block.base] = new Map();
        missingLcgs[block.base].set?.(20, lcgsInfoOf20);

        remainingBaysLength = remainingBaysLength - sep - blockSizeInMm;
      }
    } else {
      remainingPartsLength -= vp.len;
    }
  });

  // Calcs the missing 20 ft LCGs
  return {
    missingLcgs: missingLcgs,
  };
}

export type TAllBayLcgs = Record<IBayPattern, Map<Partial<TContainerLengths>, ILcgsTriplet>>;

export interface ILcgsTriplet {
  aftLcg: number;
  lcg: number;
  foreLcg: number;
}
